import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    Box, Paper, Typography, TextField, Button, Alert, Chip, Snackbar,
    CircularProgress, IconButton, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { getCustomerWithSales, setCustomerIc } from '../../api/customerApi';
import { formatPhoneNumber, openWhatsApp } from '../../utils/phoneUtils';
import { icBirthday, icAge } from '../../utils/icBirthday';
import { formatIc, icInput } from '../../utils/icFormat';

/**
 * One customer, full width — the same shape as the sale page and the referrer page:
 * a back link, a title, then labelled cards in two columns.
 * (user 2026-10-21: "specific customer page do like sales page like that full width,
 *  but donnid put any sales thing. just can direct link to sales id")
 *
 * Replaces the old dialog, which could not be linked to, shared, or opened at all by
 * an agent — the list had no customer id to give it, because the sales API never sent
 * one. That is fixed on the API side; this page is what the id now opens.
 *
 * NO sales detail here, deliberately: the sale ids link straight to the sale, and
 * anything else repeated on this page would be a second version of numbers the sale
 * page owns. (user: "donnid put any sales thing. just can direct link to sales id")
 */

const PP = "'Poppins', sans-serif";

const cardSx = {
    p: { xs: 2, md: 2.5 }, borderRadius: '14px', border: '1px solid #E2E8F0',
    backgroundColor: '#fff',
};
const headSx = {
    fontFamily: PP, fontSize: '0.72rem', fontWeight: 700, color: '#64748B',
    textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5,
};
const gridSx = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 };

function ReadOnly({ label, value, action }) {
    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontFamily: PP, fontSize: '0.68rem', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                {label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                <Typography sx={{ fontFamily: PP, fontSize: '0.9rem', color: '#0F172A', fontWeight: 600, overflowWrap: 'anywhere' }}>
                    {value || '—'}
                </Typography>
                {action}
            </Box>
        </Box>
    );
}

export default function CustomerPage() {
    const { customerId } = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [ic, setIc] = useState('');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    // Back to whichever list they came in from, not a hardcoded one.
    const base = pathname.startsWith('/accounting') ? '/accounting/customers' : '/sales/customers';

    const load = useCallback(() => {
        setLoading(true);
        setError('');
        getCustomerWithSales(customerId)
            .then(res => {
                setData(res.data);
                setIc(formatIc(res.data?.ic));
            })
            .catch(() => setError('Could not load that customer.'))
            .finally(() => setLoading(false));
    }, [customerId]);

    useEffect(() => { load(); }, [load]);

    const saveIc = async () => {
        setSaving(true);
        setError('');
        try {
            await setCustomerIc({ customerId: Number(customerId), ic });
            setSaved(true);
            load();
        } catch (err) {
            const d = err?.response?.data;
            setError((typeof d === 'string' && d.trim()) || d?.message || 'Could not save that.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    if (!data) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Alert severity="error" sx={{ fontFamily: PP }}>{error || 'Not found.'}</Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(base)}
                    sx={{ fontFamily: PP, textTransform: 'none', mt: 2 }}>
                    Back to All Customer
                </Button>
            </Box>
        );
    }

    // The birthday is never stored; it is the first six digits of the IC, read on the
    // way to the screen. Blank where the IC is missing or is not a real date.
    const birthday = icBirthday(data.ic);
    const age = icAge(data.ic);
    const sales = Array.isArray(data.sales) ? data.sales : [];
    const icDirty = (ic || '') !== (data.ic || '');
    const typedBirthday = icBirthday(ic);

    const fullAddress = [
        data.address,
        [data.postcode, data.city].filter(s => s && String(s).trim()).join(' '),
        data.state,
    ].filter(s => s && String(s).trim()).join(', ');

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(base)}
                sx={{ fontFamily: PP, textTransform: 'none', color: '#64748B', mb: 1 }}>
                All Customer
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1.35rem', color: '#0F172A' }}>
                    {data.customerName || `Customer #${data.customerId}`}
                </Typography>
                <Chip size="small" label={sales.length === 1 ? '1 sale' : `${sales.length} sales`}
                    sx={{ fontFamily: PP, fontWeight: 700, fontSize: '0.68rem', backgroundColor: '#E0F2FE', color: '#075985' }} />
                {birthday && (
                    <Chip size="small" label={`Born ${birthday}`}
                        sx={{ fontFamily: PP, fontWeight: 700, fontSize: '0.68rem', backgroundColor: '#FEF3C7', color: '#92400E' }} />
                )}
            </Box>

            {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <Paper elevation={0} sx={cardSx}>
                        <Typography sx={headSx}>Contact</Typography>
                        <Box sx={gridSx}>
                            <ReadOnly label="Name" value={data.customerName} />
                            <ReadOnly
                                label="Phone"
                                value={formatPhoneNumber(data.phoneNumber)}
                                action={data.phoneNumber ? (
                                    <Tooltip title={`WhatsApp ${formatPhoneNumber(data.phoneNumber)}`}>
                                        <IconButton size="small" onClick={() => openWhatsApp(data.phoneNumber, '')}
                                            sx={{ p: 0.25, color: '#128C7E' }}>
                                            <WhatsAppIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                ) : null}
                            />
                            <ReadOnly label="Email" value={data.email} />
                            <ReadOnly label="Customer ID" value={data.customerId} />
                        </Box>
                    </Paper>

                    <Paper elevation={0} sx={cardSx}>
                        <Typography sx={headSx}>IC &amp; birthday</Typography>
                        <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#94A3B8', mb: 1.5 }}>
                            The birthday is read from the IC — 930503 is 3 May 1993 — so it is never
                            typed separately and cannot disagree with the number on the agreement.
                        </Typography>
                        <Box sx={gridSx}>
                            <TextField size="small" label="IC Number" value={ic}
                                onChange={e => setIc(icInput(e.target.value))} fullWidth
                                placeholder="e.g. 930503105624"
                                helperText={
                                    ic
                                        ? (typedBirthday ? `Birthday: ${typedBirthday}` : 'No readable date in that number')
                                        : 'Empty clears it'
                                }
                            />
                            <ReadOnly
                                label="Birthday"
                                value={birthday ? `${birthday}${age != null ? ` · ${age} yrs` : ''}` : null}
                            />
                        </Box>
                        <Box sx={{ mt: 2 }}>
                            <Button variant="contained" size="small" startIcon={<SaveIcon />}
                                disabled={!icDirty || saving} onClick={saveIc}
                                sx={{ fontFamily: PP, textTransform: 'none' }}>
                                {saving ? 'Saving…' : 'Save IC'}
                            </Button>
                        </Box>
                    </Paper>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                    <Paper elevation={0} sx={cardSx}>
                        <Typography sx={headSx}>Address</Typography>
                        <ReadOnly label="Full address" value={fullAddress} />
                        <Box sx={{ ...gridSx, mt: 2 }}>
                            <ReadOnly label="City" value={data.city} />
                            <ReadOnly label="State" value={data.state} />
                            <ReadOnly label="Postcode" value={data.postcode} />
                            <ReadOnly label="Customer since" value={
                                data.createdOn ? String(data.createdOn).slice(0, 10) : null
                            } />
                        </Box>
                    </Paper>

                    <Paper elevation={0} sx={cardSx}>
                        <Typography sx={headSx}>Sales</Typography>
                        {sales.length === 0 ? (
                            <Typography sx={{ fontFamily: PP, fontSize: '0.82rem', color: '#64748B' }}>
                                No sales on this customer.
                            </Typography>
                        ) : (
                            <>
                                <Typography sx={{ fontFamily: PP, fontSize: '0.75rem', color: '#94A3B8', mb: 1.5 }}>
                                    Straight to the sale. Everything about it — price, kWp, status,
                                    documents — lives on its own page, not repeated here.
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {sales.map(sale => (
                                        <Chip
                                            key={sale.salesId}
                                            label={`Sale #${sale.salesId}`}
                                            onClick={() => navigate(`/accounting/sales/${sale.salesId}`)}
                                            sx={{
                                                fontFamily: PP, fontWeight: 600, fontSize: '0.78rem',
                                                backgroundColor: '#F1F5F9', color: '#1A8CFF',
                                                '&:hover': { backgroundColor: '#E0F2FE' },
                                            }}
                                        />
                                    ))}
                                </Box>
                            </>
                        )}
                    </Paper>
                </Box>
            </Box>

            <Snackbar open={saved} autoHideDuration={2500} onClose={() => setSaved(false)}
                message="Saved" anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
        </Box>
    );
}
